// Mock data for standalone frontend testing

// Utility function to generate consistent task IDs
export const generateTaskId = () => {
  let taskCounter = parseInt(localStorage.getItem('taskCounter') || '0', 10);
  taskCounter += 1;
  localStorage.setItem('taskCounter', taskCounter.toString());
  return `task-${taskCounter}`;
};

// Initialize task counter based on existing tasks
export const initializeTaskCounter = () => {
  if (!localStorage.getItem('taskCounterInitialized')) {
    // Get all tasks from localStorage or use default mock data
    const storedPending = localStorage.getItem('mockPendingTasks');
    const storedAssigned = localStorage.getItem('mockAssignedTasks');
    const storedCompleted = localStorage.getItem('mockCompletedTasks');
    
    const allPending = storedPending ? JSON.parse(storedPending) : mockPendingTasks;
    const allAssigned = storedAssigned ? JSON.parse(storedAssigned) : mockAssignedTasks;
    const allCompleted = storedCompleted ? JSON.parse(storedCompleted) : mockCompletedTasks;
    
    // Find the highest task number from all existing tasks
    let maxNumber = 0;
    
    [...allPending, ...allAssigned, ...allCompleted].forEach(task => {
      if (task._id) {
        // Handle both 'task-X' and 'completed-X', 'assigned-X' formats
        let num = 0;
        if (task._id.startsWith('task-')) {
          num = parseInt(task._id.replace('task-', ''), 10);
        } else if (task._id.startsWith('completed-')) {
          num = parseInt(task._id.replace('completed-', ''), 10);
        } else if (task._id.startsWith('assigned-')) {
          num = parseInt(task._id.replace('assigned-', ''), 10);
        }
        if (!isNaN(num) && num > maxNumber) {
          maxNumber = num;
        }
      }
    });
    
    // Set counter to max number found, so next ID will be maxNumber + 1
    localStorage.setItem('taskCounter', maxNumber.toString());
    localStorage.setItem('taskCounterInitialized', 'true');
  }
};

export const mockPendingTasks = [
  {
    _id: 'task-1',
    address: '123 Main Street, City',
    date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    time: '10:00',
    hours: 3,
    cleaningType: 'residential',
    comments: 'Please focus on kitchen and bathrooms',
    status: 'pending',
    client: { name: 'John Doe', email: 'john@example.com' }
  },
  {
    _id: 'task-2',
    address: '456 Oak Avenue, City',
    date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    time: '14:00',
    hours: 5,
    cleaningType: 'commercial',
    comments: 'Office cleaning, 3 floors',
    status: 'pending',
    client: { name: 'Jane Smith', email: 'jane@example.com' }
  },
  {
    _id: 'task-3',
    address: '789 Pine Road, City',
    date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    time: '09:00',
    hours: 4,
    cleaningType: 'deep',
    comments: 'Move-out cleaning required',
    status: 'pending',
    client: { name: 'Bob Johnson', email: 'bob@example.com' }
  }
];

export const mockCleaners = [
  {
    _id: 'cleaner-001',
    name: 'Sarah Williams',
    email: 'sarah@cleaner.com',
    phone: '555-0101',
    experience: 5
  },
  {
    _id: 'cleaner-002',
    name: 'Mike Thompson',
    email: 'mike@cleaner.com',
    phone: '555-0102',
    experience: 3
  },
  {
    _id: 'cleaner-003',
    name: 'Emily Davis',
    email: 'emily@cleaner.com',
    phone: '555-0103',
    experience: 7
  }
];

export const mockCompletedTasks = [
  {
    _id: 'task-4',
    address: '123 Main Street, City',
    date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    time: '10:00',
    hours: 3,
    actualHours: 3.5,
    cleaningType: 'residential',
    status: 'completed',
    clientId: 'client-001',
    client: { name: 'John Doe', email: 'john@example.com' },
    cleaner: { name: 'Sarah Williams', _id: 'cleaner-001' },
    rating: 5,
    ratingComment: 'Excellent service! Very thorough and professional.'
  },
  {
    _id: 'task-5',
    address: '456 Oak Avenue, City',
    date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    time: '14:00',
    hours: 5,
    actualHours: 5,
    cleaningType: 'commercial',
    status: 'completed',
    clientId: 'client-001',
    client: { name: 'Jane Smith', email: 'jane@example.com' },
    cleaner: { name: 'Mike Thompson', _id: 'cleaner-002' }
    // No rating yet - client can rate this job
  },
  
  {
    _id: 'task-6',
    address: '555 Maple Drive, City',
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    time: '15:00',
    hours: 3,
    actualHours: 3,
    cleaningType: 'move',
    status: 'completed',
    clientId: 'client-001',
    client: { name: 'Current User', email: 'user@example.com' },
    cleaner: { name: 'Mike Thompson', _id: 'cleaner-002' }
    // No rating yet - client can rate this job
  }
];

export const mockAssignedTasks = [
  {
    _id: 'task-7',
    address: '123 Main Street, City',
    date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    time: '10:00',
    hours: 3,
    cleaningType: 'residential',
    comments: 'Please focus on kitchen and bathrooms',
    status: 'assigned',
    clientId: 'client-001',
    client: { name: 'John Doe', email: 'john@example.com' },
    cleaner: { name: 'Sarah Williams', _id: 'cleaner-001' }
  },
  {
    _id: 'task-8',
    address: '456 Oak Avenue, City',
    date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
    time: '14:00',
    hours: 5,
    cleaningType: 'commercial',
    comments: 'Office cleaning, 3 floors',
    status: 'accepted',
    clientId: 'client-001',
    client: { name: 'Jane Smith', email: 'jane@example.com' },
    cleaner: { name: 'Mike Thompson', _id: 'cleaner-002' }
  }
];

export const mockJobSeekers = [
  {
    _id: 'jobseeker-1',
    name: 'Alice Brown',
    email: 'alice@example.com',
    phone: '555-0201',
    address: '123 Main Street, Copenhagen, Denmark',
    experience: 2,
    status: 'pending',
    description: 'I have been working as a professional cleaner for the past 2 years, specializing in residential and commercial cleaning. I am detail-oriented, reliable, and take pride in delivering excellent results. I am available for both regular maintenance cleaning and deep cleaning services. I am also certified in using eco-friendly cleaning products.',
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    cv: '/uploads/cv-alice.pdf',
    documents: ['/uploads/id-alice.pdf', '/uploads/cert-alice.pdf']
  },
  {
    _id: 'jobseeker-2',
    name: 'Tom Wilson',
    email: 'tom@example.com',
    phone: '555-0202',
    address: '456 Oak Avenue, Aarhus, Denmark',
    experience: 4,
    status: 'pending',
    description: 'With over 4 years of experience in the cleaning industry, I have worked with various clients including offices, homes, and commercial spaces. I am punctual, professional, and have excellent time management skills. I am looking for opportunities to work with a reputable cleaning service company.',
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    cv: '/uploads/cv-tom.pdf',
    documents: ['/uploads/id-tom.pdf']
  },
  {
    _id: 'jobseeker-3',
    name: 'Lisa Anderson',
    email: 'lisa@example.com',
    phone: '555-0203',
    address: '789 Pine Road, Odense, Denmark',
    experience: 1,
    status: 'pending',
    description: 'I am a motivated individual looking to start a career in professional cleaning. Although I have limited professional experience, I have been cleaning my own home and helping friends and family for years. I am a quick learner, hardworking, and eager to gain more experience in the field.',
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    cv: '/uploads/cv-lisa.pdf',
    documents: []
  }
];

// Store mock data in localStorage for persistence across components
export const initMockData = () => {
  // Initialize task counter first
  initializeTaskCounter();
  
  // Always update completed tasks to ensure latest data with unrated tasks
  localStorage.setItem('mockCompletedTasks', JSON.stringify(mockCompletedTasks));
  
  if (!localStorage.getItem('mockDataInitialized')) {
    localStorage.setItem('mockPendingTasks', JSON.stringify(mockPendingTasks));
    localStorage.setItem('mockCleaners', JSON.stringify(mockCleaners));
    localStorage.setItem('mockAssignedTasks', JSON.stringify(mockAssignedTasks));
    localStorage.setItem('mockJobSeekers', JSON.stringify(mockJobSeekers));
    localStorage.setItem('mockDataInitialized', 'true');
  }
};

export const getMockPendingTasks = () => {
  const stored = localStorage.getItem('mockPendingTasks');
  return stored ? JSON.parse(stored) : mockPendingTasks;
};

export const getMockCleaners = () => {
  const stored = localStorage.getItem('mockCleaners');
  return stored ? JSON.parse(stored) : mockCleaners;
};

export const getMockCompletedTasks = () => {
  const stored = localStorage.getItem('mockCompletedTasks');
  return stored ? JSON.parse(stored) : mockCompletedTasks;
};

export const getMockAssignedTasks = () => {
  const stored = localStorage.getItem('mockAssignedTasks');
  return stored ? JSON.parse(stored) : mockAssignedTasks;
};

export const getMockJobSeekers = () => {
  const stored = localStorage.getItem('mockJobSeekers');
  return stored ? JSON.parse(stored) : mockJobSeekers;
};

export const updateMockPendingTasks = (tasks) => {
  localStorage.setItem('mockPendingTasks', JSON.stringify(tasks));
};

export const updateMockCompletedTasks = (tasks) => {
  localStorage.setItem('mockCompletedTasks', JSON.stringify(tasks));
};

export const updateMockAssignedTasks = (tasks) => {
  localStorage.setItem('mockAssignedTasks', JSON.stringify(tasks));
};

export const updateMockJobSeekers = (jobSeekers) => {
  localStorage.setItem('mockJobSeekers', JSON.stringify(jobSeekers));
};

export const updateMockCleaners = (cleaners) => {
  localStorage.setItem('mockCleaners', JSON.stringify(cleaners));
};

